import { useEffect, useState, useCallback } from "react";
import { X, RotateCcw, Copy, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { MonthCalendar, todayISO, isoPlusDays } from "@/components/jk/MonthCalendar";
import { Chip, SectionLabel, Spinner } from "@/components/jk/kit";
import { PrimaryButton } from "@/components/jk/ui";
import { formatTime, weekdayShort, formatDate } from "@/lib/format";
import api from "@/lib/api";

const TIMES = Array.from({ length: 15 }, (_, i) => `${String(i + 7).padStart(2, "0")}:00`); // 07:00..21:00
const DURATIONS = [30, 45, 60, 90];
const BUFFERS = [0, 5, 10, 15];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index = python weekday (Mon=0..Sun=6)
// JS Sun=0..Sat=6 -> Python weekday Mon=0..Sun=6 (matches backend day_of_week)
const pyWeekday = (iso) => (new Date(iso + "T00:00:00").getDay() + 6) % 7;
const wkey = (day, time) => `${day}|${time}`;

export default function AvailabilityManager({ onReady, client = api, basePath = "/astrologer/availability" }) {
  const minDate = todayISO();
  const maxDate = isoPlusDays(90);

  const [weekly, setWeekly] = useState({});       // pyWeekday -> [times]
  const [overrides, setOverrides] = useState({}); // iso -> { type, slots }
  const [duration, setDuration] = useState(45);
  const [buffer, setBuffer] = useState(15);
  const [loading, setLoading] = useState(true);

  const [sheet, setSheet] = useState(null);       // selected date iso (per-date override sheet)
  const [draft, setDraft] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [weeklyDraft, setWeeklyDraft] = useState(new Set()); // "day|time"
  const [weeklyDay, setWeeklyDay] = useState(0);

  const loadOverrides = useCallback(() =>
    client.get(`${basePath}/overrides`, { params: { start: minDate, end: maxDate } })
      .then(({ data }) => {
        const map = {};
        (data.overrides || []).forEach((o) => { map[o.date] = { type: o.type, slots: o.slots || [] }; });
        setOverrides(map);
      }), [minDate, maxDate, client, basePath]);

  const loadWeekly = useCallback(() =>
    client.get(basePath).then(({ data }) => {
      const map = {};
      (data.slots || []).forEach((s) => { (map[s.day_of_week] = map[s.day_of_week] || []).push(s.start_time); });
      setWeekly(map);
      if (data.duration_minutes) setDuration(data.duration_minutes);
      if (data.buffer_minutes != null) setBuffer(data.buffer_minutes);
    }), [client, basePath]);

  useEffect(() => {
    Promise.all([loadWeekly(), loadOverrides()]).catch(() => {}).finally(() => setLoading(false));
  }, [loadWeekly, loadOverrides]);

  useEffect(() => {
    if (!onReady) return;
    const hasWeekly = Object.values(weekly).some((a) => a.length > 0);
    const hasCustom = Object.values(overrides).some((o) => o.type === "custom" && o.slots.length > 0);
    onReady(hasWeekly || hasCustom);
  }, [weekly, overrides, onReady]);

  const weeklyCount = Object.values(weekly).reduce((a, arr) => a + arr.length, 0);

  const effectiveTimes = (iso) => {
    const ov = overrides[iso];
    if (ov) return ov.type === "block" ? [] : ov.slots;
    return (weekly[pyWeekday(iso)] || []).slice().sort();
  };

  // ---- per-date override sheet ----
  const openSheet = (iso) => { setSheet(iso); setDraft(new Set(effectiveTimes(iso))); };
  const closeSheet = () => setSheet(null);
  const toggleSlot = (t) => setDraft((prev) => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const persistSettings = () => client.patch(`${basePath}/settings`, { duration_minutes: duration, buffer_minutes: buffer });

  const save = async () => {
    setSaving(true);
    try {
      const slots = [...draft].sort();
      await client.post(`${basePath}/override`, { date: sheet, type: slots.length === 0 ? "block" : "custom", slots });
      await persistSettings();
      await loadOverrides();
      toast.success(slots.length === 0 ? "Day blocked — no bookings" : "Availability saved");
      closeSheet();
    } catch { toast.error("Could not save. Try again."); } finally { setSaving(false); }
  };

  const clearOverride = async () => {
    setSaving(true);
    try {
      await client.delete(`${basePath}/override/${sheet}`);
      await persistSettings();
      await loadOverrides();
      toast.success("Reverted to weekly schedule");
      closeSheet();
    } catch { toast.error("Could not clear. Try again."); } finally { setSaving(false); }
  };

  // ---- weekly default editor ----
  const openWeekly = () => {
    const d = new Set();
    Object.entries(weekly).forEach(([day, times]) => times.forEach((t) => d.add(wkey(Number(day), t))));
    setWeeklyDraft(d);
    setWeeklyDay(0);
    setWeeklyOpen(true);
  };
  const toggleWeekly = (t) => setWeeklyDraft((prev) => { const k = wkey(weeklyDay, t); const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const copyToAllDays = () => setWeeklyDraft((prev) => {
    const todays = TIMES.filter((t) => prev.has(wkey(weeklyDay, t)));
    const n = new Set(prev);
    DAYS.forEach((_, d) => todays.forEach((t) => n.add(wkey(d, t))));
    return n;
  });
  const dayHasWeekly = (d) => [...weeklyDraft].some((k) => k.startsWith(`${d}|`));

  const saveWeekly = async () => {
    setSaving(true);
    try {
      const slots = [...weeklyDraft].map((k) => { const [d, t] = k.split("|"); return { day_of_week: Number(d), start_time: t }; });
      await client.post(basePath, { slots, duration_minutes: duration, buffer_minutes: buffer });
      await loadWeekly();
      toast.success("Weekly hours saved");
      setWeeklyOpen(false);
    } catch { toast.error("Could not save weekly hours."); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  const ov = sheet ? overrides[sheet] : null;
  const markers = Object.fromEntries(Object.entries(overrides).map(([iso, o]) => [iso, o.type]));
  const weeklyDayCount = TIMES.filter((t) => weeklyDraft.has(wkey(weeklyDay, t))).length;

  return (
    <div className="pb-6" data-testid="availability-manager">
      {/* Weekly default hours card */}
      <button onClick={openWeekly} data-testid="weekly-default-card"
        className="mb-4 flex w-full items-center gap-3 rounded-[16px] border border-[#e9e6e1] bg-white p-4 text-left transition-colors hover:bg-[#faf8f5] active:scale-[0.99]">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f2f0ec] text-[#111]">
          <CalendarRange size={18} strokeWidth={1.9} />
        </span>
        <div className="flex-1">
          <p className="text-[14px] font-semibold text-[#111]">Weekly default hours</p>
          <p className="text-[12px] text-[#666]" data-testid="weekly-default-summary">
            {weeklyCount > 0 ? `${weeklyCount} recurring slot${weeklyCount === 1 ? "" : "s"} · ${duration} min · ${buffer === 0 ? "no buffer" : `${buffer} min buffer`}` : "Not set yet — tap to set your recurring hours"}
          </p>
        </div>
        <span className="text-[13px] font-semibold text-[#111]">Edit</span>
      </button>

      <p className="mb-4 text-[13px] leading-relaxed text-[#666]">
        Tap any date to customise or block it. Dates without an override follow your weekly schedule.
      </p>
      <MonthCalendar value={sheet} onSelect={openSheet} minDate={minDate} maxDate={maxDate} markers={markers} testid="availability-calendar" />

      <div className="mt-4 flex items-center gap-4 text-[12px] text-[#666]">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#FF4D00]" /> Custom hours</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#d23b2f]" /> Blocked</span>
      </div>

      {/* Weekly default editor sheet */}
      {weeklyOpen && (
        <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={() => setWeeklyOpen(false)} data-testid="weekly-sheet">
          <div className="max-h-[92%] w-full overflow-y-auto rounded-t-[24px] bg-[var(--jk-paper)] p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[20px] font-bold text-[#111111]">Weekly default hours</h2>
              <button onClick={() => setWeeklyOpen(false)} className="text-[#666666]" data-testid="weekly-close"><X size={22} /></button>
            </div>

            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {DAYS.map((d, i) => (
                <button key={d} data-testid={`weekly-day-${d.toLowerCase()}`} onClick={() => setWeeklyDay(i)}
                  className={`relative flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-[14px] text-[13px] font-semibold transition-colors ${weeklyDay === i ? "bg-[#111111] text-white" : "bg-[#f2f0ec] text-[#666666]"}`}>
                  {d}
                  {dayHasWeekly(i) && <span className={`absolute bottom-1.5 h-1 w-1 rounded-full ${weeklyDay === i ? "bg-white" : "bg-[#FF4D00]"}`} />}
                </button>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between">
              <SectionLabel>Available times</SectionLabel>
              {weeklyDayCount > 0 && (
                <button onClick={copyToAllDays} data-testid="weekly-copy-all" className="flex items-center gap-1 text-[12px] font-semibold text-[#111111]">
                  <Copy size={13} strokeWidth={2} /> Copy to all days
                </button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="weekly-time-grid">
              {TIMES.map((t) => (
                <Chip key={t} full selected={weeklyDraft.has(wkey(weeklyDay, t))} onClick={() => toggleWeekly(t)} testid={`weekly-slot-${weeklyDay}-${t}`}>
                  {formatTime(t)}
                </Chip>
              ))}
            </div>

            <div className="mt-6">
              <SectionLabel className="mb-2.5">Default session length</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => (<Chip key={d} selected={duration === d} onClick={() => setDuration(d)} testid={`weekly-dur-${d}`}>{d} min</Chip>))}
              </div>
            </div>
            <div className="mt-6">
              <SectionLabel className="mb-2.5">Buffer between sessions</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {BUFFERS.map((b) => (<Chip key={b} selected={buffer === b} onClick={() => setBuffer(b)} testid={`weekly-buffer-${b}`}>{b === 0 ? "None" : `${b} min`}</Chip>))}
              </div>
            </div>

            <div className="mt-6">
              <PrimaryButton testid="weekly-save" disabled={saving} onClick={saveWeekly}>{saving ? "Saving…" : "Save weekly hours"}</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Per-date override sheet */}
      {sheet && (
        <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={closeSheet} data-testid="override-sheet">
          <div className="max-h-[90%] w-full overflow-y-auto rounded-t-[24px] bg-[var(--jk-paper)] p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-[20px] font-bold text-[#111111]">{weekdayShort(sheet)}, {formatDate(sheet)}</h2>
                <p className="mt-0.5 text-[12px] text-[#666]" data-testid="override-status">
                  {ov?.type === "block" ? "Blocked — no bookings"
                    : ov?.type === "custom" ? `Custom hours · ${ov.slots.length} slot${ov.slots.length === 1 ? "" : "s"}`
                    : `Weekly schedule${(weekly[pyWeekday(sheet)] || []).length ? ` · ${weekly[pyWeekday(sheet)].length} slots` : " · none set"}`}
                </p>
              </div>
              <button onClick={closeSheet} className="text-[#666666]" data-testid="override-close"><X size={22} /></button>
            </div>

            <div className="mb-1 flex items-center justify-between">
              <SectionLabel>Available times</SectionLabel>
              {draft.size > 0 && (
                <button data-testid="override-clear-all" onClick={() => setDraft(new Set())} className="text-[12px] font-semibold text-[#666] hover:text-[#111]">Clear all</button>
              )}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="override-time-grid">
              {TIMES.map((t) => (
                <Chip key={t} full selected={draft.has(t)} testid={`override-slot-${t}`} onClick={() => toggleSlot(t)}>{formatTime(t)}</Chip>
              ))}
            </div>
            {draft.size === 0 && (
              <p className="mt-3 text-[12px] text-[#b4502a]" data-testid="override-block-hint">No times selected — this day will be blocked.</p>
            )}

            <div className="mt-6">
              <SectionLabel className="mb-2.5">Default session length</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => (<Chip key={d} selected={duration === d} onClick={() => setDuration(d)} testid={`override-dur-${d}`}>{d} min</Chip>))}
              </div>
            </div>
            <div className="mt-6">
              <SectionLabel className="mb-2.5">Buffer between sessions</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {BUFFERS.map((b) => (<Chip key={b} selected={buffer === b} onClick={() => setBuffer(b)} testid={`override-buffer-${b}`}>{b === 0 ? "None" : `${b} min`}</Chip>))}
              </div>
            </div>

            <div className="mt-6">
              <PrimaryButton testid="override-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</PrimaryButton>
              {ov && (
                <button data-testid="override-clear" onClick={clearOverride} disabled={saving}
                  className="mt-2 flex h-[48px] w-full items-center justify-center gap-2 rounded-[16px] text-[14px] font-semibold text-[#666] transition-all active:scale-[0.99] disabled:opacity-50">
                  <RotateCcw size={16} strokeWidth={2} /> Use weekly schedule
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
